import React from "react";

/**
 * FeatureIcon — brand-tinted inline SVG icons for the Platform Features cards.
 * Each icon strokes with a periwinkle→light gradient (matching the site accent
 * tokens) so the set is consistent and on-brand, replacing the mixed emoji that
 * rendered as monochrome/blank glyphs on some platforms.
 *
 * @param {Object} props
 * @param {string} props.name  One of: branch, calibration, link, building,
 *                             cloud, factory, tree, timeline, board, people.
 */
const PATHS = {
  branch: (
    <>
      <line x1="6" y1="3" x2="6" y2="15" />
      <circle cx="18" cy="6" r="3" />
      <circle cx="6" cy="18" r="3" />
      <path d="M18 9a9 9 0 0 1-9 9" />
    </>
  ),
  calibration: (
    <>
      <path d="M3.34 19a10 10 0 1 1 17.32 0" />
      <path d="m13.4 10.6 3.1-3.1" />
      <circle cx="12" cy="14" r="1.6" />
    </>
  ),
  link: (
    <>
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </>
  ),
  building: (
    <>
      <path d="M3 21h18" />
      <path d="M5 21V7l7-4 7 4v14" />
      <path d="M9 9h.01M12 9h.01M15 9h.01M9 13h.01M12 13h.01M15 13h.01M9 17h.01M12 17h.01M15 17h.01" />
    </>
  ),
  cloud: <path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z" />,
  tree: (
    <>
      <rect x="9" y="2" width="6" height="4.5" rx="1" />
      <rect x="2" y="17.5" width="6" height="4.5" rx="1" />
      <rect x="16" y="17.5" width="6" height="4.5" rx="1" />
      <path d="M12 6.5v7" />
      <path d="M5 17.5v-4h14v4" />
    </>
  ),
  timeline: (
    <>
      <path d="M3 4v16" />
      <rect x="6" y="5.5" width="10" height="3.5" rx="1.2" />
      <rect x="9" y="10.5" width="11" height="3.5" rx="1.2" />
      <rect x="6" y="15.5" width="7" height="3.5" rx="1.2" />
    </>
  ),
  board: (
    <>
      <rect x="3" y="3" width="5" height="13" rx="1.2" />
      <rect x="9.5" y="3" width="5" height="18" rx="1.2" />
      <rect x="16" y="3" width="5" height="9" rx="1.2" />
    </>
  ),
  people: (
    <>
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </>
  ),
  factory: (
    <>
      <path d="M2 20a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V8l-7 5V8l-7 5V4a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z" />
      <path d="M7 18h.01M12 18h.01M17 18h.01" />
    </>
  ),
};

const FeatureIcon = ({ name }) => {
  const gradId = `feat-grad-${name}`;
  return (
    <svg
      viewBox="0 0 24 24"
      width="24"
      height="24"
      fill="none"
      stroke={`url(#${gradId})`}
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="1" y2="1">
          {/* Brand Lilac400 → Lilac200 (hard-coded here because SVG gradient
              stops can't read CSS custom properties from index.css). */}
          <stop offset="0" stopColor="#d993f5" />
          <stop offset="1" stopColor="#e7c4f5" />
        </linearGradient>
      </defs>
      {PATHS[name] || null}
    </svg>
  );
};

export default FeatureIcon;
